import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

const isProd = process.env.VERCEL_ENV === 'production';

const requiredInProd = (message: string) =>
  isProd ? z.string().min(1, message) : z.string().optional().default('');

export const env = createEnv({
  server: {
    REVALIDATE_SECRET: z.string().min(1, 'REVALIDATE_SECRET is required — revalidation endpoint is unsecured without it'),
    GOOGLE_RECAPTCHA_SECRET_KEY: z.string().optional().default(''),
    AUTH_STRING: z.string().optional().default(''),
    SHOPIFY_WEBHOOK_SECRET: z.string().min(1, 'SHOPIFY_WEBHOOK_SECRET is required — product cache invalidation will reject all requests'),
    SHOPIFY_STOREFRONT_TOKEN: z.string().min(1, 'SHOPIFY_STOREFRONT_TOKEN is required — US SSR product fetches will fail'),
    SHOPIFY_CA_STOREFRONT_TOKEN: z.string().min(1, 'SHOPIFY_CA_STOREFRONT_TOKEN is required — CA SSR product fetches will fail'),
    BRAZE_API_KEY: requiredInProd('BRAZE_API_KEY is required — newsletter signup will return 500'),
  },
  client: {
    NEXT_PUBLIC_AUTH_API_URL: z.string().optional().default(''),
    NEXT_PUBLIC_BV_ENVIRONMENT: z.string().optional().default('production'),
    NEXT_PUBLIC_GTM_ID: z.string().optional().default(''),
    NEXT_PUBLIC_DATAGRAIL_CUSTOMER_ID: z.string().optional().default(''),
    NEXT_PUBLIC_DATAGRAIL_CONTAINER_ID: z.string().optional().default(''),
    NEXT_PUBLIC_WEB_ENV: z.string().optional().default(''),
    NEXT_PUBLIC_LOCALLY_COMPANY_ID: z.coerce.number().optional().default(315697),
    NEXT_PUBLIC_LOCALLY_COMPANY_NAME: z.string().optional().default('Meater'),
    NEXT_PUBLIC_ASSET_DOMAIN: z.string().min(1, 'NEXT_PUBLIC_ASSET_DOMAIN is required — all media assets will be broken'),
    NEXT_PUBLIC_BRAZE_APP_ID: requiredInProd('NEXT_PUBLIC_BRAZE_APP_ID is required — newsletter API will fail'),
    NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN: z.string().min(1, 'NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN is required — US store will be broken'),
    NEXT_PUBLIC_SHOPIFY_PUBLIC_STOREFRONT_TOKEN: z.string().min(1, 'NEXT_PUBLIC_SHOPIFY_PUBLIC_STOREFRONT_TOKEN is required — US storefront queries will fail'),
    NEXT_PUBLIC_SHOPIFY_CA_STORE_DOMAIN: z.string().min(1, 'NEXT_PUBLIC_SHOPIFY_CA_STORE_DOMAIN is required — CA store will be broken'),
    NEXT_PUBLIC_SHOPIFY_CA_PUBLIC_STOREFRONT_TOKEN: z.string().min(1, 'NEXT_PUBLIC_SHOPIFY_CA_PUBLIC_STOREFRONT_TOKEN is required — CA storefront queries will fail'),
    NEXT_PUBLIC_SHOPIFY_API_VERSION: z.string().optional().default(''),
    NEXT_PUBLIC_REFERSION_PUBLIC_KEY: z.string().optional().default(''),
    NEXT_PUBLIC_APP_ENV: z.string().optional().default(''),
    NEXT_PUBLIC_WEBSITE_URL: z.string().optional().default('https://meater.com'),
    NEXT_PUBLIC_CONTENT_TYPE_BASEPATH: z.string().optional().default(''),
    NEXT_PUBLIC_AMPLIENCE_ENDPOINT: z.string().optional().default(''),
    NEXT_PUBLIC_RECIPE_THUMBNAIL: z.string().optional().default(''),
    NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().optional().default(''),
    NEXT_PUBLIC_SEGMENT_WRITE_KEY: z.string().optional().default(''),
    NEXT_PUBLIC_INVALID_DELIVERY_KEYS: z
      .string()
      .optional()
      .default('')
      .transform((val) => (val ? val.split(',') : [])),
    NEXT_PUBLIC_REVALIDATE_PAGE_TIME: z.coerce.number().default(14400),
    NEXT_PUBLIC_REVALIDATE_LAYOUT_TIME: z.coerce.number().default(86400),
    NEXT_PUBLIC_REVALIDATE_SEARCH_TIME: z.coerce.number().default(300),
    NEXT_PUBLIC_SHOPIFY_CACHE_REVALIDATE_SECONDS: z.coerce.number().default(3600),
  },
  runtimeEnv: {
    // Server
    REVALIDATE_SECRET: process.env.REVALIDATE_SECRET,
    GOOGLE_RECAPTCHA_SECRET_KEY: process.env.GOOGLE_RECAPTCHA_SECRET_KEY,
    AUTH_STRING: process.env.AUTH_STRING,
    SHOPIFY_WEBHOOK_SECRET: process.env.SHOPIFY_WEBHOOK_SECRET,
    SHOPIFY_STOREFRONT_TOKEN: process.env.SHOPIFY_STOREFRONT_TOKEN,
    SHOPIFY_CA_STOREFRONT_TOKEN: process.env.SHOPIFY_CA_STOREFRONT_TOKEN,
    BRAZE_API_KEY: process.env.BRAZE_API_KEY,
    // Client
    NEXT_PUBLIC_AUTH_API_URL: process.env.NEXT_PUBLIC_AUTH_API_URL,
    NEXT_PUBLIC_BV_ENVIRONMENT: process.env.NEXT_PUBLIC_BV_ENVIRONMENT,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_DATAGRAIL_CUSTOMER_ID: process.env.NEXT_PUBLIC_DATAGRAIL_CUSTOMER_ID,
    NEXT_PUBLIC_DATAGRAIL_CONTAINER_ID: process.env.NEXT_PUBLIC_DATAGRAIL_CONTAINER_ID,
    NEXT_PUBLIC_WEB_ENV: process.env.NEXT_PUBLIC_WEB_ENV,
    NEXT_PUBLIC_LOCALLY_COMPANY_ID: process.env.NEXT_PUBLIC_LOCALLY_COMPANY_ID,
    NEXT_PUBLIC_LOCALLY_COMPANY_NAME: process.env.NEXT_PUBLIC_LOCALLY_COMPANY_NAME,
    NEXT_PUBLIC_ASSET_DOMAIN: process.env.NEXT_PUBLIC_ASSET_DOMAIN,
    NEXT_PUBLIC_BRAZE_APP_ID: process.env.NEXT_PUBLIC_BRAZE_APP_ID,
    NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN: process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN,
    NEXT_PUBLIC_SHOPIFY_PUBLIC_STOREFRONT_TOKEN: process.env.NEXT_PUBLIC_SHOPIFY_PUBLIC_STOREFRONT_TOKEN,
    NEXT_PUBLIC_SHOPIFY_CA_STORE_DOMAIN: process.env.NEXT_PUBLIC_SHOPIFY_CA_STORE_DOMAIN,
    NEXT_PUBLIC_SHOPIFY_CA_PUBLIC_STOREFRONT_TOKEN: process.env.NEXT_PUBLIC_SHOPIFY_CA_PUBLIC_STOREFRONT_TOKEN,
    NEXT_PUBLIC_SHOPIFY_API_VERSION: process.env.NEXT_PUBLIC_SHOPIFY_API_VERSION,
    NEXT_PUBLIC_REFERSION_PUBLIC_KEY: process.env.NEXT_PUBLIC_REFERSION_PUBLIC_KEY,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_WEBSITE_URL: process.env.NEXT_PUBLIC_WEBSITE_URL,
    NEXT_PUBLIC_CONTENT_TYPE_BASEPATH: process.env.NEXT_PUBLIC_CONTENT_TYPE_BASEPATH,
    NEXT_PUBLIC_AMPLIENCE_ENDPOINT: process.env.NEXT_PUBLIC_AMPLIENCE_ENDPOINT,
    NEXT_PUBLIC_RECIPE_THUMBNAIL: process.env.NEXT_PUBLIC_RECIPE_THUMBNAIL,
    NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    NEXT_PUBLIC_SEGMENT_WRITE_KEY: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
    NEXT_PUBLIC_INVALID_DELIVERY_KEYS: process.env.NEXT_PUBLIC_INVALID_DELIVERY_KEYS,
    NEXT_PUBLIC_REVALIDATE_PAGE_TIME: process.env.NEXT_PUBLIC_REVALIDATE_PAGE_TIME,
    NEXT_PUBLIC_REVALIDATE_LAYOUT_TIME: process.env.NEXT_PUBLIC_REVALIDATE_LAYOUT_TIME,
    NEXT_PUBLIC_REVALIDATE_SEARCH_TIME: process.env.NEXT_PUBLIC_REVALIDATE_SEARCH_TIME,
    NEXT_PUBLIC_SHOPIFY_CACHE_REVALIDATE_SECONDS: process.env.NEXT_PUBLIC_SHOPIFY_CACHE_REVALIDATE_SECONDS,
  },
  // Skip validation in test mode so unit tests aren't blocked by missing env vars
  skipValidation: process.env.NODE_ENV === 'test',
  onValidationError: (error) => {
    console.error('Invalid environment variables:', JSON.stringify(error, null, 2));
    throw new Error('Invalid environment variables');
  },
});
